// src/components/Layout/ThemeProvider.js
import React, { useMemo, useState, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

export const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

export default function CustomThemeProvider({ children }) {
  const [mode, setMode] = useState("light");

  useEffect(() => {
    const saved = localStorage.getItem("themeMode");
    if (saved) setMode(saved);
  }, []);

// inside CustomThemeProvider
useEffect(() => {
  const handleToggle = (e) => setMode(e.detail === "dark" ? "dark" : "light");
  document.addEventListener("themeToggle", handleToggle);
  return () => document.removeEventListener("themeToggle", handleToggle);
}, []);


  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === "light" ? "dark" : "light";
          localStorage.setItem("themeMode", next);
          return next;
        });
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "light"
            ? {
                background: { default: "#f4f6f8", paper: "#fff" },
              }
            : {
                background: { default: "#121212", paper: "#1e1e1e" },
              }),
        },
        components: {
          MuiSwitch: {
            defaultProps: {
              disableRipple: true,
            },
            styleOverrides: {
              root: {
                width: 43.2,
                height: 25.6,
                padding: 0,
                overflow: "visible",
              },
              switchBase: {
                padding: 2.4,
                transitionDuration: "200ms",
                "&.Mui-checked": {
                  transform: "translateX(17.6px)",
                  color: "#fff",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#3f73dc",
                    opacity: 1,
                    border: 0,
                  },
                  "&.Mui-disabled + .MuiSwitch-track": {
                    opacity: 0.45,
                  },
                },
                "&.Mui-disabled": {
                  color: mode === "dark" ? "#94a3b8" : "#cbd5e1",
                  "& + .MuiSwitch-track": {
                    opacity: mode === "dark" ? 0.35 : 0.5,
                  },
                },
              },
              thumb: {
                boxSizing: "border-box",
                width: 20.8,
                height: 20.8,
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.22)",
              },
              track: {
                borderRadius: 12.8,
                backgroundColor: mode === "dark" ? "#475569" : "#d8dee8",
                opacity: 1,
                transition: "background-color 200ms ease",
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
